'use client'

import { Input } from 'fukku/Input'
import { Select } from 'fukku/Select'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import Image from 'next/image'
import { useEffect, useMemo, useState } from 'react'
import { cx } from 'utils/cx'

import type { InputProps } from '../Input/types'
import { findCountryInfoByCountryIso } from './helpers/findCountryInfoByCountryISO'
import { findCountryInfoByDescriptionAndPrefix } from './helpers/findCountryInfoByDescriptionAndPrefix'
import { findCountryInfoByPhonePrefix } from './helpers/findCountryInfoByPhonePrefix'
import phonePrefixes from './phonePrefixes.json' assert { type: 'json' }

import styles from './PhonePrefix.module.scss'
import text from 'fukku/styles/texts.module.scss'

export type CountryCode = (typeof phonePrefixes)['countryCodes'][number]

interface Option {
	value: string
	text: string
}

type OptionsType = Option[]

interface PhoneState {
	number: string | number
	country?: CountryCode
}

export interface PhoneDataTestIds {
	phoneNumber?: string
	countryPrefix?: string
	selectPrefix?: string
}

export interface Phone {
	phoneCountryIso: string
	phoneNumber: string | number
	phonePrefix: string
}

export type PhonePrefixProps = Omit<
	InputProps,
	'onChange' | 'value' | 'data-testid'
> & {
	readonly errorMessage?: string
	readonly errorMessageNoPlaceholder?: string
	readonly hintMessage?: string
	readonly hintMessageNoPlaceholder?: string
	readonly id: string
	readonly isMobile?: boolean
	readonly onChange?: (phone: Phone) => void
	readonly prefixFieldName?: string
	readonly prefixFieldLabel: string
	readonly phoneCountryIso?: string
	readonly phoneNumber?: string | number
	readonly phonePrefix?: string
	readonly 'data-testids'?: PhoneDataTestIds
}

/**
 * @example
 * <PhonePrefix
 *  id={id}
 *  label={label}
 *  labelFocused={labelFocused}
 *  errorMessage={errorMessage}
 * />
 */
export function PhonePrefix(props: PhonePrefixProps): React.ReactNode | null {
	const {
		country: { countryISO: defaultCountryISO },
	} = useMasterData()

	const { countryCodes } = phonePrefixes
	const {
		errorMessage,
		errorMessageNoPlaceholder,
		hintMessage,
		hintMessageNoPlaceholder,
		id,
		isMobile,
		onChange = () => {
			/* do nothing */
		},
		prefixFieldName = `${id}-prefix`,
		prefixFieldLabel,
		phoneCountryIso: initialPhoneCountryIso = defaultCountryISO,
		phoneNumber = '',
		phonePrefix,
		'data-testids': dataTestIds,
		...inputProps
	} = props

	/*
  		useMemo necessary for the form initial case where an empty string arrives
		and a default countryISO assignation is needed (the default assignment above does not
		work since it's not null or undefined).
	*/
	const phoneCountryISO = useMemo(
		() => initialPhoneCountryIso || defaultCountryISO,
		[initialPhoneCountryIso]
	)

	const initialPhoneState: PhoneState = useMemo(
		() => ({
			number: phoneNumber,
			country: phonePrefix
				? findCountryInfoByPhonePrefix(phonePrefix, phoneCountryISO)
				: findCountryInfoByCountryIso(phoneCountryISO),
		}),
		[]
	)

	const [phone, setPhone] = useState<PhoneState>(initialPhoneState)
	const [options, setOptions] = useState<OptionsType>([])
	const [forceHint, setForceHint] = useState(false)

	const handleChange = (
		newPhoneNumber: string | number,
		newPhoneCountry?: CountryCode
	) => {
		if (newPhoneCountry) {
			onChange({
				phoneNumber: newPhoneNumber,
				phonePrefix: newPhoneCountry.phonePrefix,
				phoneCountryIso: newPhoneCountry.countryIso,
			})
		}
	}

	const handleSelectChange = (value: string | number | null) => {
		if (typeof value !== 'string') {
			return
		}

		const lastSpace = value.lastIndexOf(' ')
		const description = value.substring(0, lastSpace)
		const prefix = value.substring(lastSpace + 1)
		const selected = findCountryInfoByDescriptionAndPrefix(description, prefix)
		handleChange(phone.number, selected)
	}

	const handleInputChange = (value: string | number) => {
		handleChange(value, phone.country)
	}

	const handleForceHint = () => {
		setForceHint(true)
	}

	useEffect(() => {
		const opts = countryCodes.map((country) => ({
			value: `${country.countryDescription} ${country.phonePrefix}`,
			text: `${country.countryDescription} ${country.phonePrefix}`,
		}))
		setOptions(opts)

		handleChange(phone.number, phone.country)
	}, [])

	useEffect(() => {
		const phoneCountry = findCountryInfoByCountryIso(phoneCountryISO)

		if (!phoneCountry) {
			return
		}

		if (
			phoneNumber !== phone?.number ||
			phoneCountry.phonePrefix !== phone.country?.phonePrefix ||
			phoneCountryISO !== phone.country?.countryIso
		) {
			setPhone({
				number: phoneNumber,
				country: phoneCountry,
			})
		}
	}, [phoneNumber, phoneCountryISO])

	if (!phone?.country) {
		return null
	}

	return (
		<div className={styles.phonePrefix}>
			<div className={styles.selectWrapper}>
				<input
					type='hidden'
					name={prefixFieldName}
					value={phone.country.phonePrefix}
				/>
				<Select
					id={`${prefixFieldName}CountryCode`}
					options={options}
					value={`${phone.country.countryDescription} ${phone.country.phonePrefix}`}
					onChange={handleSelectChange}
					hasError={!!errorMessage}
					onFocus={handleForceHint}
					isMobile={isMobile}
					label={prefixFieldLabel}
					data-testid={dataTestIds?.selectPrefix}
					labelHidden
				>
					<p className={`${styles.prefix} ${text.inputText}`}>
						<Image
							className={styles.img}
							src={phone.country.flagURL}
							alt={`${phone.country.countryDescription} ${phone.country.phonePrefix}`}
							width={16}
							height={11}
						/>
						<span
							data-testid={dataTestIds?.countryPrefix}
							className={cx(styles.prefixNumber, text.bodyM)}
						>
							{phone.country.phonePrefix}
						</span>
					</p>
				</Select>
			</div>
			<Input
				{...inputProps}
				data-testid={dataTestIds?.phoneNumber}
				id={`${id}-number`}
				type='tel'
				value={phone.number}
				onChange={handleInputChange}
				hintPlaceholders={{ 1: phone.country.example }}
				errorPlaceholders={{ 1: phone.country.example }}
				hintMessage={
					phone.country.example ? hintMessage : hintMessageNoPlaceholder
				}
				errorMessage={
					phone.country.example ? errorMessage : errorMessageNoPlaceholder
				}
				forceHint={forceHint}
			/>
		</div>
	)
}
